import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import startCase from 'lodash.startcase';
import isEmpty from 'lodash.isempty';
import SectionLoader from '../Loader/SectionLoader';
import BasicModalBody from '../BasicModal/BasicModalBody';
import { parseEnumType } from '../../util/helpers/enumMappers';
import {
  productLocationTypes,
  productLocationTypesByName,
} from '../../util/enum/api/productTypes';
import Paragraph from '../Paragraph/Paragraph';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import useProductPrice from '../../util/hooks/useProductPrice';
import { ReactComponent as Money } from '../../assets/images/svg/money.svg';
import { ReactComponent as Vault } from '../../assets/images/svg/vault.svg';
import { ReactComponent as Delivery } from '../../assets/images/svg/package.svg';
import { ReactComponent as Card } from '../../assets/images/svg/credit-card.svg';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import BuyInputFields from '../InputFields/BuyInputFields';
import ModalButtons from '../Modal/ModalButtons';
import {
  uButtonClear,
  uFlexColumn,
  uOutlineNone,
} from '../../assets/styles/utility';
import RowItemElement from './RowItem';
import useProductCCPrice from '../../util/hooks/useProductCCPrice';
import SelectVaultLocation from './SelectVaultLocation';
import SelectStorageType from './SelectStorageType';
import SelectAddress from './SelectAddress';
import {
  ACH_DEPOSIT_WIZARD,
  ADD_ADDRESS_WIZARD,
  IRA_DEPOSIT_WIZARD,
  IS_FUND_MODAL_OPEN,
  SIGNIFYD_SESSION_ID,
  STANDARD_DEPOSIT_WIZARD,
  STATUS_NEW_ADDRESS_ADDED,
  // WIZARD_DATA,
} from '../../constants/sessionStorage';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import AddDeliveryAddressModal from '../Modals/Address/DeliveryAddressModal';
import {
  getCurrentAccount,
  getCurrentAccountUid,
  selectAccountAddresses,
} from '../../store/selectors/accountSelectors';
import { fetchAccountAddresses } from '../../store/actions/account/accountActions';
import addressTagType, {
  ADDRESS_TAG_TYPES,
  ADDRESS_TAG_TYPES_ICONS,
} from '../../util/enum/api/addressTagType';
import {
  // selectIsLoadingByActionType,
  selectIsLoadingByActionTypes,
} from '../../store/selectors/loadingSelectors';
import {
  ACCOUNT_SETTINGS_LOADING,
  FETCH_ACCOUNT_ADDRESSES_LOADING,
} from '../../store/actions/account/accountActionConstants';
import {
  getPriceForLocation,
  getQuotesForBuyDelivery,
  getQuotesForOrderStorageBuy,
} from '../../store/actions/orders/orderActions';
import { selectLocationPrices } from '../../store/selectors/buyStorageSelectors';
import {
  selectBalancesAvailable,
  selectBalancesAvailableForTrading,
} from '../../store/selectors/portfolioBalancesSelectors';
import { fetchPortfolioBalances } from '../../store/actions/portfolio/portfolioActions';
import SelectPaymentOptions from './SelectPaymentOptions';
import { PAYMENT_METHOD_TYPES_ICONS } from '../../util/enum/api/paymentTypes';
import useGetCountries from '../../util/hooks/useGetCountries';
import { ORDER_ENTRY_TYPES } from '../../util/enum/api/orderEntryTypes';
import {
  selectProductTiers,
  // selectProductTiersError,
} from '../../store/selectors/productsSelector';
// import { PRODUCT_TIERS_LOADING } from '../../store/actions/products/productActionConstants';
import { fetchProductTiers } from '../../store/actions/products/productActions';
import { ALL_ZEROES_UID } from '../../constants/mockScenarios';
import useCheckPermissions from '../../util/hooks/useCheckPermissions';
import StandardDepositWizard from '../Modals/Deposit/StandardDepositWizard/StandardDepositWizard';
import IraDepositWizard from '../Modals/DepositWizard/IraDepositWizard';
import { IRA_ENTRUST_TYPE, IRA_EQUITY_TYPE } from '../../util/constants';
import MilleniumDepositWizard from '../Modals/DepositWizard/MilleniumDepositWizard';
import { deleteWizardKey } from '../../util/helpers/wizardHelpers';
import { useBrokerCode } from '../../util/hooks/useBrokerCode';
import { generateSessionOrderId } from '../../util/helpers/sinigfyd';
import loadSignifydDeviceFingerprintScript, {
  loadSignifydDeviceFingerprintScriptWithoutSessionId,
} from '../../signifydDeviceFingerprint';
import { useBuyFlowStepNameEvent } from '../../util/helpers/crmEventsHelper';
import {
  BUY_FLOW_ORDER_TYPE,
  BUY_FLOW_STEPS_ENUM,
} from '../../util/enum/api/crmEvents';
import UpgradeDeliveryOnlyAccount from './UpgradeDeliveryOnlyAccount';
import { buyForStorageValidationSchema } from '../../validation/buyForStorageValidationSchema';
import ErrorDrawer from './ErrorDrawer';
import useErrorDrawer from '../../util/hooks/useErrorDrawer';
import { buyForDeliveryValidationSchema } from '../../validation/buyForDeliveryValidationSchema';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { getCountryNameByIso3 } from '../../util/helpers/addressHelper';
import SuccessToast from './components/SuccessToast';
import useGetPeriodicPriceTiers from '../../util/hooks/useGetPeriodicPriceTiers';
import { PRODUCT_TIERS_LOADING } from '../../store/actions/products/productActionConstants';

const TabsContainer = styled.div`
  display: flex;
  width: 100%;
  background-color: ${themeColors.colorBackgroundSecondary};
  border-radius: ${variables.borderRadius.borderRadius};
  overflow: hidden;
  height: 44px;
  margin-bottom: ${pxToRem(16)};
`;

const TabButton = styled.button`
  ${uOutlineNone};
  ${uButtonClear};
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  font-size: ${pxToRem(16)};
  font-weight: 500;
  border: none;
  cursor: pointer;
  background-color: ${(props) =>
    props.active ? themeColors.colorBackgroundPrimary : 'transparent'};
  color: ${themeColors.colorTextPrimary};
  box-shadow: ${(props) =>
    props.active ? '0 3px 8px rgba(0,0,0,0.24)' : 'none'};
  border-radius: ${(props) =>
    props.active ? variables.borderRadius.borderRadius : '0'};
  transition: all 0.2s ease;
  margin: 4px;
`;

const Icon = styled.span`
  display: inline-block;
  margin-right: 8px;
`;

const ProductPrice = styled(Paragraph)`
  font-weight: 600;
  font-size: ${pxToRem(20)};
  line-height: 1.4;

  ${mediaBelow(variables.breakpoints.bpSm)} {
    font-size: ${pxToRemMd(14)};
  }
`;

const HeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  h3 {
    flex: 1;
  }
`;

const PriceWrapper = styled.div`
  ${uFlexColumn};
  text-align: right;

  svg {
    vertical-align: middle;
    margin-right: 4px;
  }
`;

const PriceToggle = styled(Paragraph)`
  font-size: ${pxToRem(14)};
  font-weight: 600;
  line-height: 1.4;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  ${mediaBelow(variables.breakpoints.bpSm)} {
    font-size: ${pxToRemMd(12)};
  }
`;

const FormContent = styled.div`
  display: flex;
  flex: 1;
`;

const ButtonWrapper = styled.div`
  button {
    height: ${pxToRem(44)};
  }

  ${mediaBelow(variables.breakpoints.bpSm)} {
    button {
      height: ${pxToRemMd(44)};
    }
  }
`;

const VaultTag = styled.div`
  font-size: ${pxToRem(12)};
  color: ${(props) =>
    props.segregated ? themeColors.colorWhite : themeColors.colorWhite};
  font-weight: 600;
  background-color: ${(props) =>
    props.segregated
      ? themeColors.colorButtonPrimary
      : themeColors.colorSuccess};
  border-radius: ${variables.borderRadius.borderRadiusSm};
  display: inline-flex;
  padding: ${pxToRem(4)} ${pxToRem(6)};
  justify-content: center;
  align-items: center;
  margin-left: ${pxToRem(10)};
`;

const ProductName = styled.h2`
  font-size: ${pxToRem(24)};
  font-weight: 400;
  color: ${themeColors.colorTextPrimary};
`;

const BuyModalDimmer = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 9999999;
`;

const OrderSelection = ({
  wizardData,
  isProductPage,
  setWizardData,
  isPostPaidVisible,
  handleNext,
  setWizardTitle,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  setGoBackHandler,
  stepIndex,
  currentStepIndex,
  isGS,
  openSupport,
  isSupportOpen,
  isUtmaAccount,
  isIRAAccount,
  isSegregated,
  isDeliveryOnlyAccount,
  isInstaVaultProduct,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const { storageProduct } = wizardData;
  const locations = wizardData?.storageProduct.Locations || [];
  const selectedVault = wizardData?.selectedVault || {};
  const addresses = useSelector(selectAccountAddresses);
  const accountUid = useSelector(getCurrentAccountUid);
  const locationPrices = useSelector(selectLocationPrices);
  const balance = useSelector(selectBalancesAvailable);
  const countries = useGetCountries();
  const settings = useSelector(selectSettings);
  const productTiers = useSelector(selectProductTiers);
  const brokerCode = useBrokerCode();
  const account = useSelector(getCurrentAccount);
  const balanceAvailableForTrading = useSelector(
    selectBalancesAvailableForTrading,
  );
  // const productTiersError = useSelector(selectProductTiersError);
  const { displayBasicAccountOptionsPermission } = useCheckPermissions();

  const {
    isOpen: isErrorOpen,
    errorTitle,
    errorMessage,
    showError,
    hideError,
  } = useErrorDrawer();

  const [openSelectVaultLocation, setOpenSelectVaultLocation] = useState(false);
  const [openSelectStorageType, setOpenSelectStorageType] = useState(false);
  const [openSelectAddress, setOpenSelectAddress] = useState(false);
  const [openSelectPaymentOptions, setOpenSelectPaymentOptions] = useState(
    false,
  );
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const isNewAddressAdded = retrieveFromSessionStorage(
    STATUS_NEW_ADDRESS_ADDED,
  );
  const [selectedAddress, setSelectedAddress] = useState(
    () => wizardData?.selectedAddress || {},
  );
  const paymentMethods = balance?.PaymentMethods;
  const availableForTrading = balance?.AvailableForTrading;
  const [updatedPaymentMethods, setUpdatedPaymentMethods] = useState(
    paymentMethods,
  );

  const [isLoadingGetQuotes, setIsLoadingGetQuotes] = useState(false);
  const isLoading = useSelector(
    selectIsLoadingByActionTypes([
      FETCH_ACCOUNT_ADDRESSES_LOADING,
      ACCOUNT_SETTINGS_LOADING,
      PRODUCT_TIERS_LOADING,
    ]),
  );
  const [isVaultPricesLoading, setIsVaultPricesLoading] = useState(false);
  const isSegregatedProduct = !isProductPage
    ? wizardData?.selectedPlan === 'premium'
    : isSegregated;
  const [orderValues, setOrderValues] = useState({});
  const [savingAmount, setSavingAmount] = useState(0);
  const [activeTab, setActiveTab] = useState(wizardData?.activeTab || 'vault');
  const [activeType, setActiveType] = useState(
    wizardData?.activeType || 'quantity',
  );
  const selectedPlan = wizardData?.selectedPlan || 'standard';

  const isVaultActiveTab = activeTab === 'vault';
  const balancePaymentMethodUid = ALL_ZEROES_UID;

  const {
    displayIRAEntrustOptionsPermission,
    displayIRAMilleniumOptionsPermission,
    displayIRAEquityOptionsPermission,
    displayStandardOptionsPermission,
  } = useCheckPermissions();
  const handleEventBuyFlowStep = useBuyFlowStepNameEvent();

  const [isFundsModalOpen, setIsFundsModalOpen] = useState(false);
  const [upgradeAccountPopupClicked, setUpgradeAccountPopupClicked] = useState(
    false,
  );

  const [successToast, setSuccessToast] = useState({
    isOpen: false,
    title: '',
    message: '',
  });

  const getVaultSymbol = (wizardData) => {
    const selectedVault = wizardData?.selectedVault;

    const match = locations.find(
      (loc) =>
        loc.LocationType === selectedVault?.LocationType &&
        loc.ProductCode === selectedVault?.ProductCode,
    );

    return match?.SymbolCode || null;
  };

  const symbol = getVaultSymbol(wizardData);

  const minPrice = locations?.length
    ? Math.min(...locations.map((l) => l.PricePerUnit))
    : 0;
  const firstLowestVault = locations.find(
    (l) => l.PricePerUnit === minPrice && l.LocationType !== 12,
  );

  const verifiedAddresses = addresses.filter((el) => el.IsVerified);

  const countryName = getCountryNameByIso3(
    countries,
    selectedAddress?.CountryIso3Code,
  );

  const deliveryLocation =
    !isProductPage &&
    storageProduct?.Locations.find((location) => location.LocationType === 12);

  const getMinimumOrderAmount = () => {
    if (isEmpty(wizardData) || !wizardData?.selectedVault) {
      return 0;
    }
    if (isVaultActiveTab) {
      return wizardData?.selectedVault?.BuyMinimumAmount;
    }
    return deliveryLocation?.BuyForDeliveryMinimumAmount;
  };

  useEffect(() => {
    if (stepIndex === currentStepIndex) {
      setGoBackHandler(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, currentStepIndex]);

  useEffect(() => {
    setWizardTitle(t('buyWizard.buy'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!wizardData?.selectedVault && firstLowestVault && isVaultActiveTab) {
      setWizardData({
        ...wizardData,
        selectedVault: firstLowestVault,
      });
    }
  }, [wizardData?.selectedVault, firstLowestVault, isVaultActiveTab]); //eslint-disable-line

  useEffect(() => {
    if (isDeliveryOnlyAccount) {
      setActiveTab('delivery');
    }
  }, [isDeliveryOnlyAccount]);

  const unitCCPrice = useProductCCPrice(
    wizardData?.storageProduct,
    isVaultActiveTab ? wizardData?.selectedVault : deliveryLocation,
    isProductPage,
    isVaultActiveTab,
  );
  const unitPrice = useProductPrice(
    wizardData?.storageProduct,
    isVaultActiveTab ? wizardData?.selectedVault : deliveryLocation,
    isProductPage,
    isVaultActiveTab,
  );

  const isDeliveryLocationSymbolAvailable = !isInstaVaultProduct
    ? deliveryLocation?.SymbolCode
    : true;

  useEffect(() => {
    if (symbol && isDeliveryLocationSymbolAvailable) {
      dispatch(
        fetchProductTiers({
          accountUid,
          symbol: isVaultActiveTab ? symbol : deliveryLocation?.SymbolCode,
          side: 'Buy',
          location: !isVaultActiveTab
            ? Object.keys(productLocationTypesByName).find(
                (key) => productLocationTypesByName[key] === 12,
              )
            : parseEnumType(
                productLocationTypes,
                wizardData?.selectedVault?.LocationType,
              ),
          trueTiers: true,
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, dispatch, symbol, activeTab, wizardData?.selectedVault]);

  useGetPeriodicPriceTiers(
    'priceTiers',
    account,
    !isVaultActiveTab
      ? Object.keys(productLocationTypesByName).find(
          (key) => productLocationTypesByName[key] === 12,
        )
      : parseEnumType(
          productLocationTypes,
          wizardData?.selectedVault?.LocationType,
        ),
    isVaultActiveTab ? symbol : deliveryLocation?.SymbolCode,
    'Buy',
    dispatch,
    true,
    settings?.PricingRefreshFrequencyMilliseconds,
  );

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [dispatch, accountUid]);

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchPortfolioBalances({ accountUid }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updatedPaymentMethodsList = paymentMethods?.map((paymentMethod) => {
      if (!paymentMethod.PaymentMethodUid) {
        return { ...paymentMethod, PaymentMethodUid: '1' };
      }
      return paymentMethod;
    });
    setUpdatedPaymentMethods(updatedPaymentMethodsList);
  }, [paymentMethods]);

  useEffect(() => {
    if (!paymentMethods?.length) return;
    const isDefaultPaymentMethod = paymentMethods?.find(
      (paymentMethod) => paymentMethod?.IsDefault,
    );
    const accountBalance = paymentMethods?.find(
      (paymentMethod) => paymentMethod?.PaymentMethodType === 1,
    );
    const persistedPaymentMethod = wizardData?.selectedPaymentMethod
      ? paymentMethods.find(
          (pm) =>
            pm.PaymentMethodUid ===
            wizardData.selectedPaymentMethod.PaymentMethodUid,
        )
      : null;
    let nextPaymentMethod = null;

    if (persistedPaymentMethod) {
      nextPaymentMethod = wizardData.selectedPaymentMethod;
    } else if (!isPostPaidVisible && accountBalance) {
      nextPaymentMethod = accountBalance;
    } else if (isDefaultPaymentMethod) {
      nextPaymentMethod = isDefaultPaymentMethod;
    }

    setSelectedPaymentMethod(nextPaymentMethod);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethods, isPostPaidVisible, accountUid]);

  useEffect(() => {
    setWizardData((prev) => {
      if (
        selectedPaymentMethod &&
        !paymentMethods.some(
          (pm) =>
            pm.PaymentMethodUid === selectedPaymentMethod.PaymentMethodUid,
        )
      ) {
        return {
          ...prev,
          selectedPaymentMethod: null,
        };
      }

      return {
        ...prev,
        selectedPaymentMethod,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentMethod, paymentMethods]);

  const paymentMethodRowItem = () => {
    if (selectedPaymentMethod) {
      if (selectedPaymentMethod?.PaymentMethodType === 1) {
        return t('buyWizard.payment.paymentMethods.AccountBalance');
      }
      if (selectedPaymentMethod?.PaymentMethodType === 2) {
        return `${selectedPaymentMethod?.BankName}, ${'***'}${
          selectedPaymentMethod?.BankAccountMask
        }`;
      }
      if (selectedPaymentMethod?.PaymentMethodType === 3) {
        return `${t('buyWizard.payment.paymentMethods.CreditCard')} ${'***'}${
          selectedPaymentMethod?.Last4Digits
        }`;
      }
    } else {
      return t('buyWizard.payment.emptyPaymentRow');
    }
  };

  const prevAddressesRef = useRef([]);

  useEffect(() => {
    const addressesList = brokerCode === 'HAA' ? verifiedAddresses : addresses;
    if (!addressesList || isEmpty(addressesList)) return;
    const prevList = prevAddressesRef.current;
    const hasNewAddress =
      isNewAddressAdded && addressesList.length > prevList.length;

    const defaultAddress = addressesList.find(
      (address) => address.IsDefaultMailingAddress,
    );
    const firstNonPoBoxAddress = addressesList.find(
      (address) => !address.IsPoBox,
    );
    if (hasNewAddress) {
      const newestAddress = addressesList[0];

      if (!newestAddress?.IsPoBox) {
        setSelectedAddress(newestAddress);
        setWizardData({
          ...wizardData,
          selectedAddress: newestAddress,
        });

        sessionStorage.removeItem('NewAddressAdded');
      }

      prevAddressesRef.current = addressesList;
      return;
    }

    if (!isNewAddressAdded && wizardData?.selectedAddress) {
      setSelectedAddress(wizardData.selectedAddress);
      prevAddressesRef.current = addressesList;
      return;
    }
    if (defaultAddress && !defaultAddress.IsPoBox) {
      setSelectedAddress(defaultAddress);
      prevAddressesRef.current = addressesList;
      return;
    }
    if (firstNonPoBoxAddress) {
      setSelectedAddress(firstNonPoBoxAddress);
    }
    prevAddressesRef.current = addressesList;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses, verifiedAddresses, isNewAddressAdded, brokerCode, dispatch]);

  // useEffect(() => {
  //   if (isNewAddressAdded && wizardData?.selectedAddress) {
  //     sessionStorage.removeItem('NewAddressAdded');
  //   }
  // }, [isNewAddressAdded, wizardData?.selectedAddress]);

  const isDeliveryAddressSelected = (address) => {
    if (address.AddressUid === selectedAddress?.AddressUid) {
      return true;
    }

    return false;
  };

  const [pricePerUnit, setPricePerUnit] = useState(unitPrice);
  const [priceCCPerUnit, setPriceCCPerUnit] = useState(unitCCPrice);

  useEffect(() => {
    const tierPrice = productTiers?.Tiers?.[0]?.PricePerUnit;
    const ccTierPrice = productTiers?.Tiers?.[0]?.BuyCCPricePerUnit;

    if (tierPrice > 0) {
      setPricePerUnit(tierPrice);
    }

    if (ccTierPrice > 0) {
      setPriceCCPerUnit(ccTierPrice);
    }
  }, [productTiers]); //eslint-disable-line

  // useEffect(() => {
  //   if (unitPrice !== undefined && unitPrice !== null) {
  //     setPricePerUnit(unitPrice);
  //   }
  // }, [unitPrice, dispatch]); //eslint-disable-line

  // useEffect(() => {
  //   if (unitCCPrice !== undefined && unitCCPrice !== null) {
  //     setPriceCCPerUnit(unitCCPrice);
  //   }
  // }, [unitCCPrice, dispatch]); //eslint-disable-line

  const vaultTag = () => {
    const { selectedPlan, selectedVault } = wizardData || {};

    if (selectedPlan === 'premium' || (isProductPage && isSegregated)) {
      return <VaultTag segregated>{t('buyWizard.segregated')}</VaultTag>;
    }

    if (
      (!selectedPlan || selectedPlan === 'standard') &&
      selectedVault?.PricePerUnit === minPrice
    ) {
      return <VaultTag>{t('buyWizard.bestPrice')}</VaultTag>;
    }

    return null;
  };

  useEffect(() => {
    const sessionId = retrieveFromSessionStorage(SIGNIFYD_SESSION_ID);
    const existingScript = document.getElementById('sig-api');
    if (!sessionId) {
      if (existingScript) {
        existingScript.dataset.orderSessionId = generateSessionOrderId();
        storeInSessionStorage(
          SIGNIFYD_SESSION_ID,
          existingScript.dataset.orderSessionId,
        );
        return;
      }
      loadSignifydDeviceFingerprintScript();
      const script = document.getElementById('sig-api');
      storeInSessionStorage(SIGNIFYD_SESSION_ID, script.dataset.orderSessionId);
    } else if (!existingScript) {
      loadSignifydDeviceFingerprintScriptWithoutSessionId(sessionId);
    }
  }, []);

  const DeliveryAddress = () => {
    if (!addresses || (brokerCode === 'HAA' && isEmpty(verifiedAddresses))) {
      return <Paragraph>{t('buyWizard.noAddress')}</Paragraph>;
    }
    if (addresses && selectedAddress && selectedAddress?.TagType) {
      return (
        <>
          {ADDRESS_TAG_TYPES_ICONS[selectedAddress?.TagType]}{' '}
          {selectedAddress?.TagType === ADDRESS_TAG_TYPES.OTHER
            ? selectedAddress?.TagNote
            : addressTagType[selectedAddress?.TagType]}
        </>
      );
    }
    if (addresses && selectedAddress && !selectedAddress?.TagType) {
      return selectedAddress?.AddressLine;
    }
  };

  const isSingleLocation = locations.length === 1;

  const sortedLocations = [...locationPrices].sort(
    (a, b) => a.LocationPrice - b.LocationPrice,
  );

  const fetchVaultLocationPrices = () => {
    if (!accountUid || !storageProduct) return;

    const quantityOrAmount =
      activeType === 'quantity'
        ? orderValues.QuantityAmount || 1
        : orderValues.CashAmount || 0;

    const unitType = activeType === 'quantity' ? 'Quantity' : 'Cash';
    const paymentMethodType =
      selectedPaymentMethod?.PaymentMethodType || 'AccountBalance';

    setOpenSelectVaultLocation(true);
    setIsVaultPricesLoading(true);

    dispatch(
      getPriceForLocation({
        accountUid,
        data: {
          productCode: storageProduct?.ProductCode,
          quantityOrAmount,
          unitType,
          paymentMethodType,
        },
        onSuccess: () => {
          setIsVaultPricesLoading(false);
        },
        onError: () => {
          setIsVaultPricesLoading(false);
        },
        isLoading: setIsVaultPricesLoading,
      }),
    );
  };

  const internationalAddressNotSupported = () => (
    <div>
      <div>
        {t('buyWizard.internationalAddressNotSupported.paragraph1', {
          country: countryName,
        })}
      </div>
      <div>{t('buyWizard.internationalAddressNotSupported.paragraph2')}</div>
      <div>{t('buyWizard.internationalAddressNotSupported.paragraph3')}</div>
      <div>
        {t('buyWizard.internationalAddressNotSupported.paragraph4', {
          phoneTollFree: settings?.SupportPhoneTollFree,
          phoneInternational: settings?.SupportPhoneInternational,
        })}
      </div>
    </div>
  );

  const onHandleGetQuotesError = (error) => {
    const errorCode = error?.response?.data?.Errors[0]?.Code;

    if (
      !isVaultActiveTab &&
      errorCode === 'BuyForDeliveryAmountMinimumIsNotReached'
    ) {
      showError({
        message: t('apiErrors.BuyForDeliveryAmountMinimumIsNotReached', {
          orderMinimum:
            selectedAddress?.StateRegion === 'NY'
              ? `$${settings?.BuyForDeliveryMinimums[0].Amount}`
              : `$${deliveryLocation?.BuyForDeliveryMinimumAmount}`,
        }),
      });
      return;
    }

    if (
      errorCode === 'BuyMinimumAmountIsNotReached' ||
      errorCode === 'OrderMinimumValidationError'
    ) {
      showError({
        message: t('apiErrors.BuyForDeliveryAmountMinimumIsNotReached', {
          orderMinimum: selectedVault?.BuyMinimumAmount
            ? `$${selectedVault?.BuyMinimumAmount}`
            : wizardData?.storageProduct?.Locations[0]?.BuyMinimumAmount,
        }),
      });
      return;
    }

    if (
      errorCode === 'OrderMaximumValidationError' &&
      selectedPaymentMethod?.PaymentMethodType === 2
    ) {
      showError({
        title: t('buyWizard.achOrCardLimitExceededErrorTitle'),
        message: t('buyWizard.achLimitExceededError', {
          phoneTollFree: settings?.SupportPhoneTollFree,
          phoneInternational: settings?.SupportPhoneInternational,
        }),
      });
      return;
    }

    if (
      errorCode === 'OrderMaximumValidationError' &&
      selectedPaymentMethod?.PaymentMethodType === 3
    ) {
      showError({
        title: t('buyWizard.achOrCardLimitExceededErrorTitle'),

        message: t('buyWizard.cardLimitExceededError', {
          phoneTollFree: settings?.SupportPhoneTollFree,
          phoneInternational: settings?.SupportPhoneInternational,
        }),
      });
      return;
    }

    if (errorCode === 'Order_LocationForDeliveryNotAllowed') {
      showError({
        title: t('buyWizard.internationalAddressNotSupported.title'),
        message: internationalAddressNotSupported(),
      });
      return;
    }

    showError({ message: t(`apiErrors.${errorCode}`) });
  };

  const onHandleGetQuotesSuccess = (getQuoteResult) => {
    const {
      QuoteUid,
      QuoteStatusItem: {
        QuantityExecuted,
        ClientPricePerUnit,
        TotalClientExecutionPrice,
        VolumeDiscount,
        PaymentMethodStepData,
        PaymentTypeDiscount,
        DeliveryFee,
        SalesTax,
      },
      Tiers,
      ExpiresOnUtc,
    } = getQuoteResult;
    const calculation = {
      estimatedOrderTotal: TotalClientExecutionPrice,
      newPricePerUnit: ClientPricePerUnit,
      volumeDiscount: VolumeDiscount,
      quantity: QuantityExecuted,
      paymentMethodStepData: PaymentMethodStepData,
      paymentTypeDiscount: PaymentTypeDiscount,
      deliveryFee: DeliveryFee,
      salesTax: SalesTax,
      productTiers: Tiers,
    };

    handleEventBuyFlowStep(
      account,
      BUY_FLOW_STEPS_ENUM.REVIEW_ORDER,
      wizardData?.storageProduct,
      isVaultActiveTab
        ? BUY_FLOW_ORDER_TYPE.buyForStorage
        : BUY_FLOW_ORDER_TYPE.buyForDelivery,
    );
    setWizardData({
      ...wizardData,
      selectedPaymentMethod,
      activeType,
      calculation,
      quoteID: QuoteUid,
      expiryTime: ExpiresOnUtc,
      isVaultActiveTab,
      activeTab,
      selectedAddress,
      savingAmount,
      updatedPaymentMethods,
      productTiers,
    });

    handleNext();
  };

  useEffect(() => {
    if (!isEmpty(orderValues)) {
      setWizardData({
        ...wizardData,
        orderValues,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderValues]);

  const handleSubmitClick = async (formik) => {
    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      showError({ message: firstError });
      return;
    }

    formik.submitForm();
  };

  const submitFromUpgrade = async () => {
    if (!formikRef.current) return;

    const errors = await formikRef.current.validateForm();

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      showError({ message: firstError });
      return;
    }
    formikRef.current.submitForm();
  };

  const submitForm = (values) => {
    if (
      isDeliveryOnlyAccount &&
      !upgradeAccountPopupClicked &&
      displayBasicAccountOptionsPermission
    ) {
      setUpgradeAccountPopupClicked(true);
      return;
    }
    const paymentTypeUid =
      selectedPaymentMethod?.PaymentMethodType !== 1
        ? selectedPaymentMethod?.PaymentMethodUid
        : balancePaymentMethodUid;

    const calculateMaxQuantity = () =>
      Math.floor(balanceAvailableForTrading / pricePerUnit);

    const isValueReachedMaximumLimit =
      values?.CashAmount > balanceAvailableForTrading ||
      values?.QuantityAmount > calculateMaxQuantity();

    if (
      selectedPaymentMethod?.PaymentMethodType === 3 &&
      selectedPaymentMethod?.CreditCardIsExpired
    ) {
      showError({ message: t('buyWizard.cardExpiredЕrror') });
      return;
    }
    if (
      selectedPaymentMethod?.PaymentMethodType === 1 &&
      isValueReachedMaximumLimit
    ) {
      showError({ message: t('buyWizard.insufficientBalance') });
      return;
    }

    setOrderValues({
      CashAmount: activeType === 'dollars' ? values.CashAmount : 0,
      QuantityAmount: activeType === 'quantity' ? values.QuantityAmount : 0,
    });

    const orderEntry = isPostPaidVisible
      ? {
          orderEntryType: ORDER_ENTRY_TYPES.ORDER_ENTRY,
        }
      : {};

    const payload = {
      getQuotesPayload: {
        accountUid,
        symbolCode: isVaultActiveTab
          ? selectedVault?.SymbolCode
          : deliveryLocation?.SymbolCode,
        quantityOrAmount:
          activeType === 'dollars'
            ? values?.CashAmount
            : values?.QuantityAmount,
        sideType: 'Buy',
        unitType: activeType === 'dollars' ? 'Cash' : 'Quantity',
        paymentType: selectedPaymentMethod?.PaymentMethodType,
        addressUid: selectedAddress?.AddressUid,
      },
      isLoading: setIsLoadingGetQuotes,
      onSuccess: onHandleGetQuotesSuccess,
      onError: onHandleGetQuotesError,
      isSegregated: isSegregatedProduct,
      paymentTypeUid,
      addPaymentMethodData: true,
      ...orderEntry,
    };
    setIsLoadingGetQuotes(true);

    if (isVaultActiveTab) {
      dispatch(
        getQuotesForOrderStorageBuy({
          ...payload,
        }),
      );
    } else {
      dispatch(
        getQuotesForBuyDelivery({
          ...payload,
        }),
      );
    }
  };

  if (isAddAddressOpen) {
    return (
      <AddDeliveryAddressModal
        isOpen={isAddAddressOpen}
        onClose={() => {
          removeFromSessionStorage(ADD_ADDRESS_WIZARD);
          setIsAddAddressOpen(false);
        }}
        title={t('buyWizard.addYourDeliveryAddress')}
        page="buy"
        avoidClearingStorage
        isAddAddressOpen={isAddAddressOpen}
        setIsAddAddressOpen={setIsAddAddressOpen}
        setSuccessToast={setSuccessToast}
      />
    );
  }

  return (
    <>
      <SectionLoader isLoading={isLoading || isLoadingGetQuotes}>
        <BasicModalBody style={{ position: 'relative' }}>
          {!isSingleLocation &&
            !isIRAAccount &&
            !isUtmaAccount &&
            !isProductPage &&
            !isDeliveryOnlyAccount && (
              <TabsContainer>
                <TabButton
                  active={activeTab === 'vault'}
                  onClick={() => setActiveTab('vault')}
                >
                  <Icon>
                    <Vault />
                  </Icon>
                  {t('buyWizard.vault')}
                </TabButton>
                <TabButton
                  active={activeTab === 'delivery'}
                  onClick={() => setActiveTab('delivery')}
                >
                  <Icon>
                    <Delivery />
                  </Icon>
                  {t('buyWizard.delivery')}
                </TabButton>
              </TabsContainer>
            )}
          <HeadingWrapper>
            <ProductName
              marginBottom={4}
            >{`${storageProduct?.ShortCaption}`}</ProductName>
            <PriceWrapper>
              <ProductPrice>
                {selectedPaymentMethod?.PaymentMethodType !== 3
                  ? formatMoneyNumeral(pricePerUnit)
                  : formatMoneyNumeral(priceCCPerUnit)}
              </ProductPrice>

              {/* <ProductPrice>
              {formatMoneyNumeral(storageProduct.ProductPrice)}
            </ProductPrice> */}
              {isPostPaidVisible && !isUtmaAccount && (
                <PriceToggle>
                  {selectedPaymentMethod?.PaymentMethodType === 3 ? (
                    <>
                      <Card width={20} height={20} />
                      {t('buyWizard.card')}
                    </>
                  ) : (
                    <>
                      <Money width={20} height={20} />
                      {t('buyWizard.cashAchTag')}
                    </>
                  )}
                </PriceToggle>
              )}
            </PriceWrapper>
          </HeadingWrapper>

          <Formik
            innerRef={formikRef}
            initialValues={{
              CashAmount: wizardData?.orderValues?.CashAmount || '',
              QuantityAmount: wizardData?.orderValues?.QuantityAmount || '',
              applyMax: !isPostPaidVisible,
            }}
            enableReinitialize
            validationSchema={
              isVaultActiveTab
                ? buyForStorageValidationSchema(
                    activeType,
                    selectedPaymentMethod?.PaymentMethodType !== 3
                      ? pricePerUnit
                      : priceCCPerUnit,
                    balanceAvailableForTrading,
                    productLocationTypes,
                    selectedVault,
                    getMinimumOrderAmount,
                  )
                : buyForDeliveryValidationSchema({
                    activeType,
                    unitPrice:
                      selectedPaymentMethod?.PaymentMethodType !== 3
                        ? pricePerUnit
                        : priceCCPerUnit,
                    balanceAvailableForTrading,
                    getMinimumOrderAmount,
                    selectedAddress,
                    buyForDeliveryMinimums: settings?.BuyForDeliveryMinimums,
                    settings,
                  })
            }
            onSubmit={submitForm}
            validateOnBlur={false}
          >
            {(formik) => {
              const { values, setFieldValue, setFieldTouched } = formik;
              return (
                <Form>
                  <FormContent>
                    <BuyInputFields
                      activeType={activeType}
                      setActiveType={setActiveType}
                      values={values}
                      setFieldValue={setFieldValue}
                      selectedVault={wizardData?.selectedVault}
                      setFieldTouched={setFieldTouched}
                      unitPrice={pricePerUnit}
                      priceCCPerUnit={priceCCPerUnit}
                      setOrderValues={setOrderValues}
                      // displayError={displayError}
                      // setGetQuotesError={setGetQuotesError}
                      locationForTiers={parseEnumType(
                        productLocationTypes,
                        wizardData?.selectedVault?.LocationType,
                      )}
                      symbol={symbol}
                      setPricePerUnit={setPricePerUnit}
                      setPriceCCPerUnit={setPriceCCPerUnit}
                      // onValuesChange={setFormValues}
                      selectedPaymentMethod={selectedPaymentMethod}
                      setSavingAmount={setSavingAmount}
                      productTiers={productTiers}
                      isGS={isGS}
                      isPostPaidVisible={isPostPaidVisible}
                      availableForTrading={availableForTrading}
                    />
                  </FormContent>
                  {!isInstaVaultProduct && (
                    <RowItemElement
                      label={
                        activeTab === 'delivery'
                          ? t('buyWizard.delivery')
                          : t('buyWizard.vault')
                      }
                      value={
                        activeTab === 'delivery' ? (
                          DeliveryAddress()
                        ) : (
                          <>
                            <Vault />
                            {startCase(
                              parseEnumType(
                                productLocationTypes,
                                wizardData?.selectedVault?.LocationType,
                              ),
                            )}
                            {vaultTag()}
                          </>
                        )
                      }
                      onClick={() => {
                        if (activeTab === 'vault') {
                          fetchVaultLocationPrices();
                        } else {
                          setOpenSelectAddress(true);
                        }
                      }}
                      onClickValue={() => {
                        if (
                          !addresses ||
                          (brokerCode === 'HAA' && isEmpty(verifiedAddresses))
                        ) {
                          setIsAddAddressOpen(true);
                        }
                      }}
                      isNotButton={
                        (activeTab === 'delivery' &&
                          (!addresses ||
                            (brokerCode === 'HAA' &&
                              isEmpty(verifiedAddresses)))) ||
                        isProductPage ||
                        (brokerCode === 'GS' && isIRAAccount) ||
                        isSingleLocation
                      }
                    />
                  )}

                  <RowItemElement
                    label={t('buyWizard.payWith')}
                    value={
                      <>
                        {
                          PAYMENT_METHOD_TYPES_ICONS[
                            selectedPaymentMethod?.PaymentMethodType
                          ]
                        }
                        {paymentMethodRowItem()}
                        {/* {selectedPaymentMethod &&
                        selectedPaymentMethod?.PaymentMethodType !== 3 && (
                          <Tag>{t('buyWizard.payment.savePrecent')}</Tag>
                        )} */}
                      </>
                    }
                    onClick={() => {
                      if (isPostPaidVisible || isUtmaAccount) {
                        setOpenSelectPaymentOptions(true);
                      } else {
                        setIsFundsModalOpen(true);
                        // setWizardContent(IS_FUND_MODAL_OPEN, true);
                      }
                    }}
                    clickLabel={
                      !isPostPaidVisible && !isUtmaAccount
                        ? t('buyWizard.addFunds')
                        : t('buyWizard.changeLabel')
                    }
                  />
                  {!upgradeAccountPopupClicked && (
                    <ButtonWrapper>
                      <ModalButtons
                        isVertical
                        showOnlyPrimary
                        onClick={() => handleSubmitClick(formik)}
                        primaryButtonProps={{
                          type: 'button',

                          label: t('buyWizard.reviewBuy'),
                          textTransform: 'capitalize',
                          marginTop: '10',
                          disabled:
                            (activeType === 'quantity' &&
                              !values.QuantityAmount) ||
                            (activeType === 'dollars' && !values.CashAmount) ||
                            !selectedPaymentMethod ||
                            !selectedVault ||
                            !selectedAddress,
                          // disabled:
                          //   (activeType === 'Quantity' &&
                          //     selectedVault.IsCashOrderOnly) ||
                          //   errors?.CashAmount ||
                          //   errors?.QuantityAmount,
                          'data-cy': 'button-next',
                        }}
                      />
                    </ButtonWrapper>
                  )}

                  <ErrorDrawer
                    open={isErrorOpen}
                    errorTitle={errorTitle}
                    errorMessage={errorMessage}
                    onClose={hideError}
                  />
                </Form>
              );
            }}
          </Formik>
          {isFundsModalOpen && <BuyModalDimmer />}
        </BasicModalBody>
      </SectionLoader>

      <SuccessToast
        isOpen={successToast.isOpen}
        title={successToast.title}
        message={successToast.message}
        onClose={() => setSuccessToast((prev) => ({ ...prev, isOpen: false }))}
      />

      <SelectVaultLocation
        open={openSelectVaultLocation}
        setOpen={setOpenSelectVaultLocation}
        wizardData={wizardData}
        setWizardData={setWizardData}
        lowestVault={firstLowestVault}
        setOpenSelectStorageType={setOpenSelectStorageType}
        accountUid={accountUid}
        sortedLocations={sortedLocations}
        isLoading={isVaultPricesLoading}
        locations={locations}
        isIRAAccount={isIRAAccount}
      />
      <SelectStorageType
        open={openSelectStorageType}
        setOpen={setOpenSelectStorageType}
        wizardData={wizardData}
        setWizardData={setWizardData}
        setOpenSelectVaultLocation={setOpenSelectVaultLocation}
        selectedPlan={selectedPlan}
      />
      <SelectAddress
        open={openSelectAddress}
        setOpen={setOpenSelectAddress}
        addresses={addresses}
        wizardData={wizardData}
        setWizardData={setWizardData}
        setIsAddAddressOpen={setIsAddAddressOpen}
        isDeliveryAddressSelected={isDeliveryAddressSelected}
        setSelectedAddress={setSelectedAddress}
        selectedAddress={selectedAddress}
        countries={countries}
        openSupport={openSupport}
        isSupportOpen={isSupportOpen}
        setSuccessToast={setSuccessToast}
        isNewAddressAdded={isNewAddressAdded}
        // setSelectedAddress={handleConfirmAddress}
      />
      <SelectPaymentOptions
        open={openSelectPaymentOptions}
        setOpen={setOpenSelectPaymentOptions}
        wizardData={wizardData}
        setWizardData={setWizardData}
        paymentMethods={updatedPaymentMethods}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        setIsAddAddressOpen={setIsAddAddressOpen}
        isAddAddressOpen={isAddAddressOpen}
        accountUid={accountUid}
        isDeliveryOnlyAccount={isDeliveryOnlyAccount}
        setSuccessToast={setSuccessToast}
      />

      {displayStandardOptionsPermission && isFundsModalOpen && (
        <StandardDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => {
            setIsFundsModalOpen(false);
            deleteWizardKey(STANDARD_DEPOSIT_WIZARD);
            deleteWizardKey(IS_FUND_MODAL_OPEN);
            deleteWizardKey(ACH_DEPOSIT_WIZARD);
            setWizardData({ amount: 0, bankAccount: {} });
          }}
          overModal
          avoidClearingStorage
        />
      )}

      {displayIRAEquityOptionsPermission && isFundsModalOpen && (
        <IraDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => {
            setIsFundsModalOpen(false);
            deleteWizardKey(IRA_DEPOSIT_WIZARD);
            deleteWizardKey(IS_FUND_MODAL_OPEN);
            deleteWizardKey(ACH_DEPOSIT_WIZARD);
          }}
          type={IRA_EQUITY_TYPE}
          overModal
          avoidClearingStorage
        />
      )}
      {displayIRAEntrustOptionsPermission && isFundsModalOpen && (
        <IraDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => {
            setIsFundsModalOpen(false);
            deleteWizardKey(IRA_DEPOSIT_WIZARD);
            deleteWizardKey(IS_FUND_MODAL_OPEN);
            deleteWizardKey(ACH_DEPOSIT_WIZARD);
          }}
          type={IRA_ENTRUST_TYPE}
          overModal
          avoidClearingStorage
        />
      )}
      {displayIRAMilleniumOptionsPermission && isFundsModalOpen && (
        <MilleniumDepositWizard
          isModalOpen={isFundsModalOpen}
          handleClose={() => {
            setIsFundsModalOpen(false);
            deleteWizardKey(IRA_DEPOSIT_WIZARD);
            deleteWizardKey(IS_FUND_MODAL_OPEN);
            deleteWizardKey(ACH_DEPOSIT_WIZARD);
          }}
          overModal
          avoidClearingStorage
        />
      )}
      <UpgradeDeliveryOnlyAccount
        open={upgradeAccountPopupClicked}
        doItLaterClick={submitFromUpgrade}
      />
    </>
  );
};

OrderSelection.propTypes = {
  wizardData: PropTypes.shape({
    selectedPaymentMethod: PropTypes.shape({
      PaymentMethodType: PropTypes.number,
      PaymentMethodUid: PropTypes.string,
    }),
    storageProduct: PropTypes.shape({
      ProductCaption: PropTypes.string,
      ProductPrice: PropTypes.number,
      Locations: PropTypes.arrayOf(
        PropTypes.shape({
          BuyMinimumAmount: PropTypes.number,
        }),
      ),
      ProductCode: PropTypes.string,
      ShortCaption: PropTypes.string,
    }),
    activeTab: PropTypes.string,
    selectedPlan: PropTypes.string,
    activeType: PropTypes.string,
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
      PricePerUnit: PropTypes.number,
      BuyMinimumAmount: PropTypes.number,
      BuyForDeliveryMinimumAmount: PropTypes.number,
    }),
    orderValues: PropTypes.shape({
      CashAmount: PropTypes.number,
      QuantityAmount: PropTypes.number,
    }),
    selectedAddress: PropTypes.shape({}),
    paymentMethodType: PropTypes.string,
    isSegregated: PropTypes.bool,
  }),
  selectedPaymentMethod: PropTypes.shape({
    PaymentMethodType: PropTypes.number,
    PaymentMethodUid: PropTypes.number,
    Last4Digits: PropTypes.number,
    BankName: PropTypes.string,
    BankAccountMask: PropTypes.number,
    CreditCardIsExpired: PropTypes.bool,
  }),
  isProductPage: PropTypes.bool,
  setWizardData: PropTypes.func,
  isPostPaidVisible: PropTypes.bool,
  handleNext: PropTypes.func,
  setWizardTitle: PropTypes.func,
  setSelectedPaymentMethod: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  stepIndex: PropTypes.number,
  currentStepIndex: PropTypes.number,
  isGS: PropTypes.bool,
  openSupport: PropTypes.func,
  isSupportOpen: PropTypes.bool,
  isUtmaAccount: PropTypes.bool,
  isIRAAccount: PropTypes.bool,
  isSegregated: PropTypes.bool,
  isDeliveryOnlyAccount: PropTypes.bool,
  isInstaVaultProduct: PropTypes.bool,
};

export default OrderSelection;
